/**
 * Declarative config of the shared pieces card (`PiecesCard`): the piece
 * view model and its value descriptors (stable keys `libelle`, `statut`,
 * `dateDemande`), plus the upload ids a contract / claim piece carries
 * (fiches 22975 / 22976).
 */

import type { InfoItemDescriptor } from '@components/shared/configurable';

import { normaliseStatus } from './statusBadgeConfig';

/** What an uploaded document is attached to: a contract (22975) or a claim (22976). */
export type PieceUploadOrigin = 'contrat' | 'sinistre';

/**
 * The ids a piece upload needs (fiches 22975 / 22976), mapped by the domain
 * service from its `*_PMQ` row (`null` on the piece when one is missing — no
 * upload zone is offered then).
 */
export interface PieceUploadTarget {
  origin: PieceUploadOrigin;
  /** `ID_PMD` — the piece request (call 2 path `id_pmd`). */
  idPmd: number;
  /** Call 1 `idTab`: `ID_POL` (contract) / `ID_SIN` (claim). */
  idTab: number;
  /** Call 2 path parent: `ID_NPOL` (contract, `id_npol`) / `ID_SIN` (claim, `id_sin`). */
  parentId: number;
  /** `CD_DS` — call 1 `type` (document category). */
  cdDs: string;
}

/** One requested piece, mapped by the domain service from a `*_PMQ` row. */
export interface PieceItem {
  /** Stable row id (`ID_PMD`). */
  id: string;
  /** Piece label (`LIB_PIE`). */
  libelle: string | null;
  /** Request date, pre-formatted `JJ/MM/AAAA` (`DATDEMANDE_PMD`). */
  dateDemande: string | null;
  /** Raw status (`STATUT`, e.g. `EN ATTENTE`). */
  statut: string | null;
  /**
   * Upload ids (contract / claim pieces only); `null` / absent ⇒ the piece
   * cannot be uploaded (an id or `CD_DS` is missing).
   */
  upload?: PieceUploadTarget | null;
}

/** Raw `STATUT` of a piece still awaited. */
export const PENDING_PIECE_STATUT = 'EN ATTENTE';

/** Whether the piece is still awaited (`STATUT` « EN ATTENTE », trim / case / accent tolerant). */
export function isPendingPiece(piece: Pick<PieceItem, 'statut'>): boolean {
  return piece.statut !== null && normaliseStatus(piece.statut) === PENDING_PIECE_STATUT;
}

/** The raw ids of an upload target (`null` = missing in the VEOS row). */
export interface PieceUploadTargetIds {
  idPmd: number | null;
  idTab: number | null;
  parentId: number | null;
  cdDs: string | null;
}

/** Build an upload target, or `null` when one of its ids / `CD_DS` is missing. */
export function buildPieceUploadTarget(
  origin: PieceUploadOrigin,
  { idPmd, idTab, parentId, cdDs }: PieceUploadTargetIds,
): PieceUploadTarget | null {
  if (idPmd === null || idTab === null || parentId === null || cdDs === null || cdDs === '') {
    return null;
  }
  return { origin, idPmd, idTab, parentId, cdDs };
}

/** Stable keys of a piece's displayed values. */
export type PieceItemKey = 'libelle' | 'statut' | 'dateDemande';

/** A piece value descriptor (override-able surface + display flags). */
export interface PieceItemField extends InfoItemDescriptor {
  key: PieceItemKey;
  /** `primary` = title line (with the badge), `secondary` = muted line below. */
  line: 'primary' | 'secondary';
  /** Render the value as a status badge. */
  badge?: boolean;
  /** Prefix the value with `label` (« Demandé le 30/09/2026 »). */
  showLabel?: boolean;
}

/** Base descriptors (design order). */
export const PIECE_ITEM_FIELDS: readonly PieceItemField[] = [
  { key: 'libelle', label: 'Pièce', order: 0, line: 'primary' },
  { key: 'statut', label: 'Statut', order: 1, line: 'primary', badge: true },
  { key: 'dateDemande', label: 'Demandé le', order: 2, line: 'secondary', showLabel: true },
];

/** The display text of a (non-badge) descriptor for a piece. */
export function pieceFieldText(field: PieceItemField, piece: PieceItem): string {
  const value = piece[field.key]?.trim() ?? '';
  if (value === '' || !field.showLabel || !field.label) return value;
  return `${field.label} ${value}`;
}
